import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import CaseGuideTemplateEditor from "../../../components/dashboard/cases/guides/CaseGuideTemplateEditor";
import { fetchCaseTypes } from "../../../api/case/type";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";

export const CaseGuidesSettingsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initializedRef = useRef(false);
  const [caseTypesState, setCaseTypesState] = useState({
    isLoading: true,
    data: [],
  });
  const [scope, setScope] = useState("general");

  useEffect(() => {
    fetchCaseTypes({
      page: 1,
      per_page: 200,
      query: "",
      sort: "asc",
      sortBy: "title",
      view: "all",
      filters: [],
    })
      .then((response) => {
        const rows = response?.data?.data || [];
        setCaseTypesState({
          isLoading: false,
          data: rows,
        });
      })
      .catch(() => setCaseTypesState({ isLoading: false, data: [] }));
  }, []);

  useEffect(() => {
    if (caseTypesState.isLoading) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    const fromUrl = searchParams.get("case_type_id");
    if (fromUrl === "general") {
      setScope("general");
      return;
    }
    if (fromUrl && caseTypesState.data.some((row) => String(row.id) === String(fromUrl))) {
      setScope(String(fromUrl));
      return;
    }
    setScope("general");
    setSearchParams({ case_type_id: "general" }, { replace: true });
  }, [caseTypesState.isLoading, caseTypesState.data, searchParams, setSearchParams]);

  const onScopeChange = (nextScope) => {
    setScope(nextScope);
    setSearchParams(nextScope === "general" ? { case_type_id: "general" } : { case_type_id: nextScope }, {
      replace: true,
    });
  };

  const selectedCaseType = useMemo(
    () => caseTypesState.data.find((item) => String(item.id) === String(scope)),
    [caseTypesState.data, scope]
  );

  useEffect(() => {
    if (caseTypesState.isLoading) return;
    if (scope !== "general" && !selectedCaseType) {
      setScope("general");
      setSearchParams({ case_type_id: "general" }, { replace: true });
    }
  }, [caseTypesState.isLoading, scope, selectedCaseType, setSearchParams]);

  const generalOnly = scope === "general";

  return (
    <DashboardHeader
      variant="v2"
      title={t("Caseguider")}
      subtitle={t("Opprett og rediger guidede flyter for saker.")}
      backUrl="/dashboard/cases/settings"
      actions={[]}
      meta={[]}
    >
      <div className="w-full max-w-none px-4 py-6 pb-10 sm:px-6 lg:px-8">
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-950">{t("Guideomfang case")}</h2>
              <p className="mt-1 text-sm text-slate-600">{t("Case guides scope intro")}</p>
            </div>
            <Link
              to="/dashboard/cases/settings"
              className="inline-flex items-center justify-center rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("Tilbake til caseinnstillinger")}
            </Link>
          </div>

          {caseTypesState.isLoading ? (
            <div className="mt-4">
              <ResourceLoading />
            </div>
          ) : (
            <>
              <select
                value={scope}
                onChange={(event) => onScopeChange(event.target.value)}
                className="mt-4 block w-full max-w-xl rounded-md border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
              >
                <option value="general">{t("Generelle caseguider")}</option>
                {caseTypesState.data.map((caseType) => (
                  <option key={caseType.id} value={caseType.id}>
                    {caseType.title || caseType.displayTitle || `#${caseType.id}`}
                  </option>
                ))}
              </select>
              {caseTypesState.data.length === 0 ? (
                <p className="mt-2 text-sm text-slate-600">{t("Ingen case typer er opprettet ennå.")}</p>
              ) : null}
            </>
          )}
        </div>

        {!caseTypesState.isLoading ? (
          <CaseGuideTemplateEditor
            key={generalOnly ? "general" : selectedCaseType?.id || "type"}
            caseType={generalOnly ? null : selectedCaseType}
            generalOnly={generalOnly}
            fullPageLayout
          />
        ) : null}
      </div>
    </DashboardHeader>
  );
};
